import React, { useState, useEffect, useCallback } from "react";
import { Pagination } from "@mui/material";
import { API_URLS } from "../../../apiUrl";
import StreetEdit from "../../../editModals/streetEdit";
import axiosInstance from "../../../components/axiosInstance";

interface StreetItem {
  ward_no: string;
  street_name: string;
  street_primary_id: string;
}

interface StreetListProps {
  muniCipalityName: string;
  muniCipalityId: string;
}

const ITEMS_PER_PAGE = 50;

const StreetList: React.FC<StreetListProps> = ({ muniCipalityName, muniCipalityId }) => {
  const [streetTableData, setStreetTableData] = useState<StreetItem[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedStreet, setSelectedStreet] = useState({
    name: "",
    id: "",
  });

  const fetchStreets = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const response = await axiosInstance.post(API_URLS.streetList, {
        municipalityTbl_id: muniCipalityId,
        page: currentPage,
        limit: ITEMS_PER_PAGE,
      });

      setStreetTableData(response.data.data.data || []);
      setTotalItems(response.data.data.total || 0);
    } catch (err) {
      setError("Something went wrong!");
      console.error("Error fetching streets:", err);
    } finally {
      setLoading(false);
    }
  }, [muniCipalityId, currentPage]);

  useEffect(() => {
    fetchStreets();
  }, [fetchStreets]);

  const filteredData = streetTableData.filter((item) =>
    item.street_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    item.ward_no?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handlePageChange = (_event: React.ChangeEvent<unknown>, value: number) => {
    setCurrentPage(value);
  };

  const openModal = (streetName: string, streetId: string) => {
    setSelectedStreet({ name: streetName, id: streetId });
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  const totalPages = Math.ceil(totalItems / ITEMS_PER_PAGE);

  return (
    <div className="container mx-auto">
      {/* Header and Search Section */}
      <div className="flex gap-5 mb-4">
        <h1 className="text-2xl font-semibold text-gray-800 dark:text-gray-100">
          Street List ({muniCipalityName})
        </h1>

        <div className="flex justify-end md:w-4/12 w-full ml-auto">
          <input
            type="text"
            placeholder="Search..."
            className="border px-3 py-2 rounded-lg w-full text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-900 dark:bg-gray-800"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-4 p-4 bg-red-100 text-red-700 rounded-lg">
          {error}
        </div>
      )}

      {/* Table */}
      <div className="overflow-x-auto bg-white rounded-lg shadow-lg border border-gray-200 dark:border-gray-800">
        <table className="min-w-full table-auto">
          <thead className="bg-gray-100 border-b dark:border-gray-600 dark:bg-gray-700">
            <tr>
              <th className="py-3 px-6 text-left text-sm font-medium text-gray-600 dark:text-gray-100">
                Ward
              </th>
              <th className="py-3 px-6 text-left text-sm font-medium text-gray-600 dark:text-gray-100">
                Street
              </th>
              <th className="py-3 px-6 text-center text-sm font-medium text-gray-600 dark:text-gray-100">
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={3} className="py-3 px-6 text-center">
                  Loading...
                </td>
              </tr>
            ) : filteredData.length === 0 ? (
              <tr>
                <td colSpan={3} className="py-3 px-6 text-center dark:bg-gray-800">
                  No results found
                </td>
              </tr>
            ) : (
              filteredData.map((item, index) => (
                <tr
                  key={item.street_primary_id || index}
                  className="border-b dark:border-gray-700 hover:bg-gray-50"
                >
                  <td className="py-3 px-6 text-sm text-gray-800 dark:text-gray-200 dark:bg-gray-600">
                    {item.ward_no}
                  </td>
                  <td className="py-3 px-6 text-sm text-gray-800 dark:text-gray-200 dark:bg-gray-600">
                    {item.street_name}
                  </td>
                  <td className="py-3 px-6 text-sm text-gray-800 dark:text-gray-200 dark:bg-gray-600 text-center">
                    <div className="flex gap-2 justify-center">
                      <button
                        onClick={() => openModal(item.street_name, item.street_primary_id)}
                        className="px-2 py-1.5 text-green-500 border border-green-200 rounded bg-green-100 hover:bg-green-200 transition-colors"
                        title="Edit Street"
                      >
                        <i className="ri-edit-line"></i>
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {filteredData.length > 0 && (
        <div className="flex justify-center mt-4">
          <Pagination
            count={totalPages}
            page={currentPage}
            onChange={handlePageChange}
            color="primary"
          />
        </div>
      )}

      {/* Edit Modal */}
      <StreetEdit
        isOpen={isModalOpen}
        closeModal={closeModal}
        muniCipalityName={muniCipalityName}
        muniCipalityId={muniCipalityId}
        streetName={selectedStreet.name}
        streetId={selectedStreet.id}
      />
    </div>
  );
};

export default StreetList;